import { buildEwayBillPayload } from '../../services/orders/eway-bill';
import { useState, useEffect, useRef } from "react";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { Calendar } from "primereact/calendar";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { EwayFormModel, DeliveryDetailsModel } from "../../models/Delivery-Executive/DeliveryExecutive";
import orderServices from "../../services/orders/orderservice";
import { Toast, ToastMessage } from "primereact/toast";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { Column } from "primereact/column";
import UserAddressService from '../../services/useraddress/useraddress.service';
import { StateDropdownDTO } from '../../models/cust-address/cust-address-model';
import posService from "../../services/pos/pos.service";

interface EwayFormProps {
  order: DeliveryDetailsModel;
  onCancel: () => void;
  onSuccess?: () => Promise<void> | void;
}

const POSEWayBill: React.FC<EwayFormProps> = ({ order, onCancel, onSuccess }) => {
  const [supplyTypes, setSupplyTypes] = useState<{ label: string; value: string }[]>([]);
  const [subTypes, setSubTypes] = useState<{ label: string; value: string }[]>([]);
  const [docTypes, setDocTypes] = useState<{ label: string; value: string }[]>([]);
  const [transactionTypes, setTransactionTypes] = useState<{ label: string; value: string }[]>([]);
  const [transportModes, setTransportModes] = useState<{ label: string; value: string }[]>([]);
  const [vehicleTypes, setVehicleTypes] = useState<{ label: string; value: string }[]>([]);
  const [showProductsDialog, setShowProductsDialog] = useState(false);
  const [products, setProducts] = useState<any[]>([]);
  const [states, setStates] = useState<StateDropdownDTO[]>([]);
  const [stateFilter, setStateFilter] = useState('');
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [challanCache, setChallanCache] = useState<any>(null);
  const GSTIN = process.env.REACT_APP_GSTIN || "";

  const toast = useRef<Toast>(null);

  const showMessage = (label: string, message: string, severity: ToastMessage['severity']) => {
    toast.current?.show({ severity, summary: label, detail: message, life: severity === 'error' ? 10000 : 3000 });
  };

  const [ewayForm, setEwayForm] = useState<EwayFormModel>({
    supplyType: "",
    subType: "",
    docType: "",
    docNo: "",
    docDate: new Date(),
    transactionType: "",
    billShipType: "BillTo",
    billFrom: {
      name: "Desiri Naturals Private Limited",
      gstin: GSTIN,
      state: "",
      address: "",
      place: "",
      pincode: "",
      stateGstCode: 0,
    },
    billTo: { name: "", gstin: "", state: "", stateGstCode: 0 },
    shipTo: { address: "", place: "", pincode: "", state: "", gstin: "", stateGstCode: 0 },
    transport: {
      mode: "",
      vehicleType: "",
      vehicleNo: "",
      transporterId: "",
      transporterName: "",
      distance: 0,
    },
  });

  type NestedKeys = "billFrom" | "billTo" | "shipTo" | "transport";

  const handleEwayChange = (field: string, value: any, parent?: NestedKeys) => {
    if (parent) {
      setEwayForm(prev => ({
        ...prev,
        [parent]: { ...(prev[parent] as object), [field]: value },
      }));
      setErrors(prev => ({ ...prev, [`${parent}.${field}`]: "" }));
    } else {
      setEwayForm(prev => {
        const updated = { ...prev, [field]: value };

        // ✅ Prefill for Sub Type = 11 (existing)
        if (field === "subType" && String(value) === "11") {
          const billFrom = prev.billFrom;
          updated.billTo = {
            name: "Un Registered Persons (URP)/ Retail Customers",
            gstin: billFrom.gstin,
            state: billFrom.state,
            stateGstCode: billFrom.stateGstCode,
          };
          updated.shipTo = {
            address: "",
            place: "",
            pincode: "",
            state: "",
            gstin: "",
            stateGstCode: 0,
          };
        }

        // ✅ Prefill for Sub Type = 5 using cached challan
        if (field === "subType" && String(value) === "5" && challanCache) {
          const isSubType5 = String(value) === "5";
          if (!isSubType5) {
            updated.billTo = { name: "", gstin: "", state: "", stateGstCode: 0 };
            updated.shipTo = {
              address: "",
              place: "",
              pincode: "",
              state: "",
              gstin: "",
              stateGstCode: 0,
            };
          }

          // ✅ Apply Bill To ONLY if subtype = 5 and challan exists
          if (isSubType5 && challanCache) {
            return applyChallanAddresses(challanCache, updated, true);
          }
        }

        // ✅ Reset when subtype not 11 or 5
        if (field === "subType" && !["11", "5"].includes(String(value))) {
          updated.billTo = { name: "", gstin: "", state: "", stateGstCode: 0 };
          updated.shipTo = { address: "", place: "", pincode: "", state: "", gstin: "", stateGstCode: 0 };
        }

        return updated;
      });
    }
  };

  // ✅ Fetch POS (bill from) once
  useEffect(() => {
    const getPOSDetails = async () => {
      try {
        const response = await orderServices.getPOS();
        if (response && response.length > 0) {
          setEwayForm(prev => ({
            ...prev,
            billFrom: {
              name: "Desiri Naturals Private Limited",
              gstin: GSTIN,
              state: response[0].stateName,
              address: `${response[0].address1}${response[0].address2 ? ', ' + response[0].address2 : ''}${response[0].landmark ? ', ' + response[0].landmark : ''}`,
              place: response[0].cityName,
              pincode: response[0].zipcode,
              stateGstCode: response[0].stateGstCode,
            },
          }));
        }
      } catch (error) {
        console.error("Error fetching POS:", error);
      }
    };
    getPOSDetails();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchStates(stateFilter);
    }, 300);
    return () => clearTimeout(timer);
  }, [stateFilter]);

  const fetchStates = async (keyword: string = '') => {
    try {
      const response = await UserAddressService.searchStates({ keyword });
      setStates(response);
    } catch {
      setStates([]);
    }
  };

  const fetchDropdownData = async (
    apiMethod: (filter: { keyword: string }) => Promise<any[]>,
    setState: React.Dispatch<React.SetStateAction<{ label: string; value: any }[]>>,
    keyword = "",
    fieldName?: string,
    parent?: NestedKeys
  ) => {
    try {
      const response = await apiMethod({ keyword });
      const formatted = (response || []).map((item: any) => ({
        label: item.Name || item.name || "Unknown",
        value: item.code ?? item.Code ?? item.id ?? 0,
      }));
      setState(formatted);

      if (formatted.length === 1 && fieldName) {
        handleEwayChange(fieldName, formatted[0].value, parent);
      }
    } catch (error) {
      console.error("Failed to fetch dropdown data:", error);
      showMessage("Error", "Failed to load dropdown data", "error");
      setState([]);
    }
  };

  const applyChallanAddresses = (
    challan: any,
    prev: EwayFormModel,
    applyBillTo: boolean
  ): EwayFormModel => {
    const item = challan?.lineItems?.[0];
    if (!item) return prev;

    const updated: EwayFormModel = {
      ...prev,

      // ✅ Bill From ALWAYS from challan
      billFrom: {
        name: "Desiri Naturals Private Limited",
        gstin: prev.billFrom.gstin,
        state: item.fromState || "",
        address: `${item.fromAddress1 || ""}${item.fromAddress2 ? ", " + item.fromAddress2 : ""}`,
        place: item.fromCity || "",
        pincode: item.fromPincode || "",
        stateGstCode: item.fromStateGstCode || 0,
      },
    };

    // ✅ Bill To / Ship To ONLY when subtype = 5
    if (applyBillTo) {
      updated.billTo = {
        name: "Desiri Naturals Private Limited",
        gstin: prev.billFrom.gstin,
        state: item.toState || "",
        stateGstCode: item.toStateGstCode || 0,
      };

      updated.shipTo = {
        address: `${item.toAddress1 || ""}${item.toAddress2 ? ", " + item.toAddress2 : ""}`,
        place: item.toCity || "",
        pincode: item.toPincode || "",
        state: item.toState || "",
        gstin: prev.billFrom.gstin,
        stateGstCode: item.toStateGstCode || 0,
      };
    }

    return updated;
  };

  // ✅ Fetch challan products only once and cache
  const fetchChallanProducts = async (showDialog = false) => {
    try {
      if (challanCache && !showDialog) return; // already cached

      const today = new Date();
      const startOfDay = new Date(today.setHours(0, 0, 0, 0));
      const endOfDay = new Date(today.setHours(23, 59, 59, 999));

      const response: any = await posService.viewOutwardChallan(startOfDay, endOfDay);
      const challans = Array.isArray(response) ? response : response?.data || [];
      const eligibleChallans = challans.filter((c: any) => !c.hasEwayBill);

      if (!eligibleChallans.length) {
        if (showDialog)
          showMessage("Info", "No E-Way Bill eligible challans found for today", "info");
        return;
      }

      const firstChallan = eligibleChallans[0];
      setChallanCache(firstChallan); // ✅ store for reuse
      setEwayForm(prev => applyChallanAddresses(firstChallan, prev, prev.subType === "5"));

      const challanNo = firstChallan?.challanNo || firstChallan?.ChallanNo || "";
      const items =
        firstChallan?.lineItems ||
        firstChallan?.products ||
        firstChallan?.ProductList ||
        [];

      if (!items.length) {
        if (showDialog) showMessage("Warning", "No products found for this challan", "warn");
        return;
      }

      // ✅ Ignore negative grossAmount values
      const filteredItems = items.filter((it: any) => Number(it.grossAmount || it.TotalAmount) >= 0);

      const normalized = filteredItems.map((it: any) => ({
        challanNo,
        productName: it.productName || it.ProductName || "",
        hsnCode: it.hsnCode || it.HSNCode || "",
        quantity: it.quantity || it.Qty || 0,
        grossAmount: it.grossAmount || it.TotalAmount || 0,
        gst: it.gst || it.GST || 0,
        taxableAmount: it.taxableAmount || it.TaxableAmount || 0,
        qtyUnit: it.qtyUnit || it.QtyUnit || "",
      }));

      setEwayForm((prev) => ({ ...prev, docNo: challanNo }));
      setProducts(normalized);

      if (showDialog) setShowProductsDialog(true);
    } catch (error) {
      console.error("Error fetching outward challan products:", error);
      if (showDialog)
        showMessage("Error", "Failed to load outward challan details", "error");
    }
  };


  useEffect(() => {
    fetchDropdownData(orderServices.searchSupplyType, setSupplyTypes, "", "supplyType");
    fetchDropdownData(orderServices.searchSubSupplyType, setSubTypes, "", "subType");
    fetchDropdownData(orderServices.searchDocumentType, setDocTypes, "", "docType");
    fetchDropdownData(orderServices.searchTransactionTypes, setTransactionTypes, "", "transactionType");
    fetchDropdownData(orderServices.searchTransportationMode, setTransportModes, "", "mode", "transport");
    fetchDropdownData(orderServices.searchVehicleType, setVehicleTypes, "", "vehicleType", "transport");
    fetchChallanProducts(false);
  }, [order]);

  const handleViewProducts = async () => {
    await fetchChallanProducts(true);
  };

  const mapToApiPayload = buildEwayBillPayload;

  const defaultFormState: EwayFormModel = {
    supplyType: "",
    subType: "",
    docType: "",
    docNo: "",
    docDate: new Date(),
    transactionType: "",
    billShipType: "BillTo",
    billFrom: {
      name: "Desiri Naturals Private Limited",
      gstin: GSTIN,
      state: "",
      address: "",
      place: "",
      pincode: "",
      stateGstCode: 0,
    },
    billTo: { name: "", gstin: "", state: "", stateGstCode: 0 },
    shipTo: { address: "", place: "", pincode: "", state: "", gstin: "", stateGstCode: 0 },
    transport: {
      mode: "",
      vehicleType: "",
      vehicleNo: "",
      transporterId: "",
      transporterName: "",
      distance: 0,
    },
  };

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    // Basic Details
    if (!ewayForm.supplyType) newErrors['supplyType'] = 'Supply Type is required';
    if (!ewayForm.subType) newErrors['subType'] = 'Sub Type is required';
    if (!ewayForm.docType) newErrors['docType'] = 'Document Type is required';
    if (!ewayForm.docNo) newErrors['docNo'] = 'Document No is required';
    if (!ewayForm.docDate) newErrors['docDate'] = 'Document Date is required';
    if (!ewayForm.transactionType) newErrors['transactionType'] = 'Transaction Type is required';

    // Bill To
    if (!ewayForm.billTo.gstin) newErrors['billTo.gstin'] = 'Bill To GSTIN is required';
    if (!ewayForm.billTo.stateGstCode) newErrors['billTo.stateGstCode'] = 'Bill To State is required';

    // Ship To
    if (!ewayForm.shipTo.pincode) newErrors['shipTo.pincode'] = 'Ship To Pincode is required';

    // Transport
    if (!ewayForm.transport.mode) newErrors['transport.mode'] = 'Transport Mode is required';
    if (!ewayForm.transport.vehicleType) newErrors['transport.vehicleType'] = 'Vehicle Type is required';
    if (!ewayForm.transport.vehicleNo) newErrors['transport.vehicleNo'] = 'Vehicle No is required';
    if (!ewayForm.transport.distance) newErrors['transport.distance'] = 'Approx. Distance is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const submitting = useRef(false);
  const [savingEwb, setSavingEwb] = useState(false);
  const handleSubmit = async () => {
    if (submitting.current) return;
    if (!validateForm()) {
      showMessage("Error", "Please fill all required fields", "error");
      return;
    }

    // ✅ Ignore negative product entries before generating payload
    const validProducts = products.filter((p) => Number(p.grossAmount) >= 0);

    if (validProducts.length === 0) {
      showMessage("Warning", "No valid (non-negative) products found for E-Way Bill", "warn");
      return;
    }

    let generatedEwb: string | undefined;
    try {
      submitting.current = true;
      setSavingEwb(true);
      const payload = mapToApiPayload(ewayForm, validProducts);
      const response = await orderServices.generateEwayBill(payload);
      const ewbNo = response?.data?.ewayBillNo || response?.data?.ewbNo;

      if (ewbNo) {
        generatedEwb = String(ewbNo);
        showMessage("Success", `E-way Bill No: ${ewbNo}`, "success");
        if (onSuccess) await onSuccess();
        setEwayForm(defaultFormState);
        setProducts([]);
        return;
      }

    } catch (failure: unknown) {
      const message = generatedEwb
        ? `E-Way Bill ${generatedEwb} was generated, but the list could not be refreshed. Refresh challans before generating again.`
        : failure instanceof Error ? failure.message : 'The E-Way Bill result could not be confirmed. Check existing bills before submitting again.';
      showMessage('Error', message, 'error');
    } finally {
      submitting.current = false;
      setSavingEwb(false);
    }
  };

  return (
    <div className="p-fluid grid">
      <Toast ref={toast} />

      {/* Basic Details */}
      <div className="col-12 mb-3">
        <Card title="Basic Details" style={{ backgroundColor: "#f2f5f7ff" }}>
          <div className="p-fluid grid">
            {/* Supply Type */}
            <div className="field col-12 md:col-6">
              <label>
                Supply Type <span className="text-red-500">*</span>
              </label>
              <Dropdown
                value={ewayForm.supplyType}
                options={supplyTypes}
                filter
                showClear
                onFilter={(e) => fetchDropdownData(orderServices.searchSupplyType, setSupplyTypes, e.filter)}
                onChange={(e) => handleEwayChange("supplyType", e.value)}
                className="w-full"
              />
              {errors['supplyType'] && <small className="p-error">{errors['supplyType']}</small>}
            </div>

            {/* Sub Type */}
            <div className="field col-12 md:col-6">
              <label>
                Sub Type <span className="text-red-500">*</span>
              </label>
              <Dropdown
                value={ewayForm.subType}
                options={subTypes}
                filter
                showClear
                onFilter={(e) => fetchDropdownData(orderServices.searchSubSupplyType, setSubTypes, e.filter)}
                onChange={(e) => handleEwayChange("subType", e.value)}
                className="w-full"
              />
              {errors['subType'] && <small className="p-error">{errors['subType']}</small>}
            </div>

            {/* Document Type */}
            <div className="field col-12 md:col-6">
              <label>
                Document Type <span className="text-red-500">*</span>
              </label>
              <Dropdown
                value={ewayForm.docType}
                options={docTypes}
                filter
                showClear
                onFilter={(e) => fetchDropdownData(orderServices.searchDocumentType, setDocTypes, e.filter)}
                onChange={(e) => handleEwayChange("docType", e.value)}
                className="w-full"
              />
              {errors['docType'] && <small className="p-error">{errors['docType']}</small>}
            </div>

            {/* Document No */}
            <div className="field col-12 md:col-6">
              <label>
                Document No <span className="text-red-500">*</span>
              </label>
              <InputText value={ewayForm.docNo} readOnly className="w-full" />
              {errors['docNo'] && <small className="p-error">{errors['docNo']}</small>}
            </div>

            {/* Document Date */}
            <div className="field col-12 md:col-6">
              <label>
                Document Date <span className="text-red-500">*</span>
              </label>
              <Calendar
                value={ewayForm.docDate}
                onChange={(e) => handleEwayChange("docDate", e.value)}
                dateFormat="dd/mm/yy"
                className="w-full"
              />
              {errors['docDate'] && <small className="p-error">{errors['docDate']}</small>}
            </div>

            {/* Transaction Type */}
            <div className="field col-12 md:col-6">
              <label>
                Transaction Type <span className="text-red-500">*</span>
              </label>
              <Dropdown
                value={ewayForm.transactionType}
                options={transactionTypes}
                filter
                showClear
                onFilter={(e) => fetchDropdownData(orderServices.searchTransactionTypes, setTransactionTypes, e.filter)}
                onChange={(e) => handleEwayChange("transactionType", e.value)}
                className="w-full"
              />
              {errors['transactionType'] && <small className="p-error">{errors['transactionType']}</small>}
            </div>
          </div>
        </Card>
      </div>

      {/* Bill From */}
      <div className="col-12 mb-3">
        <Card title="Bill From" className="flex-1 min-w-[300px]" style={{ backgroundColor: "#f7f3beff" }}>
          <div className="p-fluid grid formgrid">
            {["name", "gstin", "address", "place", "pincode", "state"].map((field) => (
              <div key={field} className="col-12 mt-2">
                <label>
                  {field.charAt(0).toUpperCase() + field.slice(1)}
                  {(field === "gstin" || field === "state" || field === "pincode") && (
                    <span className="text-red-500">*</span>
                  )}
                </label>
                <InputText
                  value={(ewayForm.billFrom as any)[field]}
                  onChange={(e) => handleEwayChange(field, e.target.value, "billFrom")}
                  readOnly
                  className="w-full"
                />
                {errors[`billFrom.${field}`] && <small className="p-error">{errors[`billFrom.${field}`]}</small>}
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Bill To / Ship To */}
      <div className="col-12 mb-3 flex flex-wrap gap-3">
        {/* Bill To */}
        <Card title="Bill To" className="flex-1 min-w-[300px]" style={{ backgroundColor: "#f1efd5ff" }}>
          <div className="p-fluid grid formgrid">
            {/* Name - not required */}
            <div className="col-12 mt-2">
              <label>Name</label>
              <InputText
                value={ewayForm.billTo.name}
                onChange={(e) => handleEwayChange("name", e.target.value, "billTo")}
                className="w-full"
              />
            </div>

            {/* GSTIN - required */}
            <div className="col-12 mt-2">
              <label>
                GSTIN <span className="text-red-500">*</span>
              </label>
              <InputText
                value={ewayForm.billTo.gstin}
                onChange={(e) => handleEwayChange("gstin", e.target.value, "billTo")}
                className="w-full"
              />
              {errors['billTo.gstin'] && <small className="p-error">{errors['billTo.gstin']}</small>}
            </div>

            {/* State - required */}
            <div className="col-12 mt-2">
              <label>
                State <span className="text-red-500">*</span>
              </label>
              <Dropdown
                value={ewayForm.billTo.stateGstCode}
                options={states.length > 0 ? states : []}
                optionLabel="display"
                optionValue="stateGstCode"
                placeholder="Select State"
                filter
                filterBy="display"
                showClear
                onFilter={(e) => setStateFilter(e.filter)}
                onChange={(e) => handleEwayChange('stateGstCode', e.value, 'billTo')}
                className="w-full"
              />
              {errors['billTo.stateGstCode'] && <small className="p-error">{errors['billTo.stateGstCode']}</small>}
            </div>
          </div>
        </Card>

        {/* Ship To */}
        <Card title="Ship To" className="flex-1 min-w-[300px]" style={{ backgroundColor: "#f1efd5ff" }}>
          <div className="p-fluid grid formgrid">
            {/* Address, Place - not required */}
            {["address", "place"].map((field) => (
              <div key={field} className="col-12 mt-2">
                <label>{field.charAt(0).toUpperCase() + field.slice(1)}</label>
                <InputText
                  value={(ewayForm.shipTo as any)[field]}
                  onChange={(e) => handleEwayChange(field, e.target.value, "shipTo")}
                  className="w-full"
                />
              </div>
            ))}

            {/* Pincode - required */}
            <div className="col-12 mt-2">
              <label>
                Pincode <span className="text-red-500">*</span>
              </label>
              <InputText
                value={ewayForm.shipTo.pincode}
                onChange={(e) => handleEwayChange("pincode", e.target.value, "shipTo")}
                className="w-full"
              />
              {errors['shipTo.pincode'] && <small className="p-error">{errors['shipTo.pincode']}</small>}
            </div>

            {/* State - not required in Ship To per validation */}
            <div className="col-12 mt-2">
              <label>State</label>
              <Dropdown
                value={ewayForm.shipTo.stateGstCode}
                options={states.length > 0 ? states : []}
                optionLabel="display"
                optionValue="stateGstCode"
                placeholder="Select State"
                filter
                filterBy="display"
                showClear
                onFilter={(e) => setStateFilter(e.filter)}
                onChange={(e) => handleEwayChange('stateGstCode', e.value, 'shipTo')}
                className="w-full"
              />
            </div>
          </div>
        </Card>
      </div>


      {/* Transport Details */}
      <Card title="Transport Details" style={{ backgroundColor: "#f8f5f6ff" }}>
        <div className="p-fluid grid">
          {[
            { field: "transporterId", label: "Transporter ID" },
            { field: "transporterName", label: "Transporter Name" },
            { field: "distance", label: "Approx. Distance (KM)" }
          ].map(({ field, label }) => (
            <div key={field} className="field col-12 md:col-4">
              <label>
                {label} <span className="text-red-500">{field === "distance" ? "*" : ""}</span>
              </label>
              <InputText
                value={(ewayForm.transport as any)[field] || ""}
                onChange={(e) => handleEwayChange(field, e.target.value, "transport")}
                className="w-full"
              />
              {field !== "transporterId" && errors[`transport.${field}`] && (
                <small className="p-error">{errors[`transport.${field}`]}</small>
              )}
            </div>
          ))}
        </div>
      </Card>


      {/* Transport Part B */}
      <div className="col-12 mb-3">
        <Card title="Part B" style={{ backgroundColor: "#faedd8ff" }}>
          <div className="p-fluid grid">
            {[
              { field: "mode", label: "Transport Mode" },
              { field: "vehicleType", label: "Vehicle Type" },
              { field: "vehicleNo", label: "Vehicle No" }
            ].map(({ field, label }) => (
              <div key={field} className="field col-12 md:col-4">
                <label>
                  {label} <span className="text-red-500">*</span>
                </label>
                {field === "vehicleNo" ? (
                  <InputText
                    value={(ewayForm.transport as any)[field] || ""}
                    onChange={(e) => handleEwayChange(field, e.target.value, "transport")}
                    className="w-full"
                  />
                ) : (
                  <Dropdown
                    value={(ewayForm.transport as any)[field]}
                    options={field === "mode" ? transportModes : vehicleTypes}
                    filter
                    showClear
                    onFilter={(e) =>
                      field === "mode"
                        ? fetchDropdownData(orderServices.searchTransportationMode, setTransportModes, e.filter)
                        : fetchDropdownData(orderServices.searchVehicleType, setVehicleTypes, e.filter)
                    }
                    onChange={(e) => handleEwayChange(field, e.value, "transport")}
                    className="w-full"
                  />
                )}
                {errors[`transport.${field}`] && <small className="p-error">{errors[`transport.${field}`]}</small>}
              </div>
            ))}

            {/* Transporter Doc No & Date - optional */}
            <div className="field col-12 md:col-6 mt-2">
              <label>Transporter Doc No</label>
              <InputText
                value={(ewayForm.transport as any).transporterDocNo || ""}
                onChange={(e) => handleEwayChange("transporterDocNo", e.target.value, "transport")}
                className="w-full"
              />
            </div>
            <div className="field col-12 md:col-6 mt-2">
              <label>Transporter Doc Date</label>
              <Calendar
                value={(ewayForm.transport as any).transporterDocDate || null}
                onChange={(e) => handleEwayChange("transporterDocDate", e.value, "transport")}
                dateFormat="dd/mm/yy"
                className="w-full"
              />
            </div>
          </div>
        </Card>
      </div>

      <div className="col-12 flex justify-end mb-3">
        <Button
          label="View Product Details"
          icon="pi pi-list"
          className="p-button-info w-auto"
          onClick={handleViewProducts}
        />
      </div>

      {/* Buttons */}
      <div className="col-12 flex justify-end gap-3">
        <Button label="Submit" icon="pi pi-check" onClick={handleSubmit} loading={savingEwb} disabled={savingEwb} />
        <Button label="Cancel" icon="pi pi-times" className="p-button-secondary" onClick={onCancel} />
      </div>

      {/* Product Dialog */}
      <Dialog
        header="Challan Product Details"
        visible={showProductsDialog}
        style={{ width: "70vw" }}
        modal
        onHide={() => setShowProductsDialog(false)}
      >
        <DataTable value={products || []} responsiveLayout="scroll">
          <Column field="productName" header="Product Name" />
          <Column field="hsnCode" header="HSN Code" />
          <Column field="quantity" header="Quantity" />
          <Column field="grossAmount" header="Total Amount" />
          <Column field="gst" header="GST" />
        </DataTable>
      </Dialog>
    </div>
  );
};

export default POSEWayBill;
